import { useState } from 'react';

import styled from '@emotion/styled';

import StatsCard from '../../../components/Cards/StartsCard';
import { Segmented } from '../../../components/Layout/PagePieces';
import { DashboardOverview } from '../../../models/dashboard/overview.model';
import { PAUSE_REASON_LABELS, SCHEDULE_KIND_LABELS } from '../../../models/schedules/schedule.model';
import { GIFTS, SCHEDULES } from '../../../routes/routeConstants';
import { ButtonLink } from '../../../components/Link/Link';
import { capitalize, formatNaira, formatNumber, tableFormatDateTimeNG } from '../../../utils/utils';

const pct = (value: number) => `${value.toFixed(1)}%`;
const money = (value: string) => formatNaira(Number(value));
const words = (value: string) => capitalize(value.replace(/_/g, ' '));

type Feature = 'schedules' | 'gifts' | 'ejor';

const FEATURE_LABELS: Record<Feature, string> = {
  schedules: 'Scheduled payments',
  gifts: 'Dash gifts',
  ejor: 'Ejor',
};

// Ejor, Dash gifts and scheduled payments for the selected period, from the
// overview's `ejor`/`gifts`/`schedules` blocks, as tabs of one card so the
// overview stays short. A block the API leaves out (feature off) isn't shown.
const FeatureMetrics: React.FC<{ overview: DashboardOverview | null }> = ({ overview }) => {
  const ejor = overview?.ejor;
  const gifts = overview?.gifts;
  const schedules = overview?.schedules;
  const available = (['schedules', 'gifts', 'ejor'] as Feature[]).filter((f) => overview?.[f]);
  const [picked, setPicked] = useState<Feature>('schedules');
  const tab = available.includes(picked) ? picked : available[0];

  if (!tab) return null;

  const link =
    tab === 'schedules'
      ? { to: `/${SCHEDULES}`, label: 'View schedules' }
      : tab === 'gifts'
        ? { to: `/${GIFTS}`, label: 'View gifts' }
        : null;

  return (
    <Block>
      <BlockHeader>
        <Segmented role='tablist' aria-label='Products'>
          {available.map((f) => (
            <button
              key={f}
              type='button'
              role='tab'
              aria-selected={tab === f}
              className={tab === f ? 'active' : ''}
              onClick={() => setPicked(f)}
            >
              {FEATURE_LABELS[f]}
            </button>
          ))}
        </Segmented>
        {link && (
          <ButtonLink to={link.to} className='more'>
            {link.label} →
          </ButtonLink>
        )}
      </BlockHeader>

      {tab === 'schedules' && schedules && (
        <>
          <Grid>
            <Tile title='Active schedules' value={formatNumber(schedules.active_schedules)}>
              {schedules.runs_waiting > 0
                ? `${schedules.runs_waiting} run(s) waiting on the VTpass wallet`
                : 'No runs waiting'}
            </Tile>
            <Tile title='Scheduled purchases' value={formatNumber(schedules.scheduled_purchases)}>
              {money(schedules.scheduled_purchase_volume)} · {pct(schedules.scheduled_share_of_purchases)} of purchases
            </Tile>
            <Tile title='Run decline rate' value={pct(schedules.run_decline_rate)}>
              Manual purchases: {pct(schedules.manual_decline_rate)}
            </Tile>
            <Tile title='Pay now recoveries' value={formatNumber(schedules.pay_now_recoveries)}>
              Paid by the user after a failed run
            </Tile>
          </Grid>
          <Breakdowns>
            <Breakdown title='Active by bill' items={schedules.active_by_bill_type} />
            <Breakdown title='Active by repeat' items={schedules.active_by_kind} labels={SCHEDULE_KIND_LABELS} />
            <Breakdown title='Paused because' items={schedules.paused_by_reason} labels={PAUSE_REASON_LABELS} />
            <Breakdown title='Runs this period' items={schedules.runs_in_period_by_status} />
          </Breakdowns>
        </>
      )}

      {tab === 'gifts' && gifts && (
        <>
          <Grid>
            <Tile title='Gifts paid' value={formatNumber(gifts.gifts_paid)}>
              {money(gifts.gifts_paid_volume)} · {gifts.airtime_count} airtime, {gifts.electricity_count} electricity
            </Tile>
            <Tile title='Money held for gifts' value={money(gifts.outstanding_liability)}>
              Paid for, not yet delivered or refunded
            </Tile>
            <Tile title='Gift fees' value={money(gifts.fees_collected)}>
              Paystack fees {money(gifts.paystack_fees)}
            </Tile>
            <Tile title='Accepted' value={pct(gifts.acceptance_rate)}>
              {gifts.distinct_senders} senders → {gifts.distinct_recipients} recipients
            </Tile>
          </Grid>
          <Breakdowns>
            <Breakdown title='By status' items={gifts.by_status} />
            <Breakdown title='Refunds by reason' items={gifts.refunds_by_reason} />
            <BreakdownBox>
              <BreakdownTitle>Electricity gift minimum</BreakdownTitle>
              <BreakdownLine>
                <span>{money(gifts.electricity_gift_minimum)}</span>
              </BreakdownLine>
              <Muted>
                VTpass limits checked{' '}
                {gifts.limits_last_refreshed ? tableFormatDateTimeNG(gifts.limits_last_refreshed) : 'never'}
              </Muted>
            </BreakdownBox>
          </Breakdowns>
        </>
      )}

      {tab === 'ejor' && ejor && (
        <>
          <Grid>
            <Tile title='Ejor payments' value={formatNumber(ejor.ejor_payment_count)}>
              {money(ejor.ejor_payment_volume)}
            </Tile>
            <Tile title='Share of sales' value={pct(ejor.ejor_volume_share)}>
              By value, this period
            </Tile>
            <Tile title='Requests paid' value={pct(ejor.completion_rate)}>
              {ejor.ejors_completed} of {ejor.ejors_sent} sent
            </Tile>
            <Tile title='People' value={formatNumber(ejor.distinct_initiators)}>
              asked · {ejor.distinct_payers} paid for someone
            </Tile>
          </Grid>
        </>
      )}
    </Block>
  );
};

export default FeatureMetrics;

const Tile: React.FC<{ title: string; value: string; children: React.ReactNode }> = ({
  title,
  value,
  children,
}) => (
  <StatsCard.Root>
    <StatsCard.Header>{title}</StatsCard.Header>
    <StatsCard.Value>{value}</StatsCard.Value>
    <StatsCard.PlainFooter>{children}</StatsCard.PlainFooter>
  </StatsCard.Root>
);

const Breakdown: React.FC<{
  title: string;
  items: Record<string, number>;
  labels?: Record<string, string>;
}> = ({ title, items, labels }) => {
  const entries = Object.entries(items).sort((a, b) => b[1] - a[1]);
  return (
    <BreakdownBox>
      <BreakdownTitle>{title}</BreakdownTitle>
      {entries.length === 0 ? (
        <Muted>None</Muted>
      ) : (
        entries.map(([key, count]) => (
          <BreakdownLine key={key}>
            <span>{labels?.[key] ?? words(key)}</span>
            <strong>{formatNumber(count)}</strong>
          </BreakdownLine>
        ))
      )}
    </BreakdownBox>
  );
};

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: var(--radius);
  box-shadow: var(--shadow-xs);
  padding: 20px;
  background-color: white;
`;

const BlockHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  align-items: center;

  & .more {
    font-size: 13px;
    color: var(--ok);
  }
`;

// Tiles sit flat inside the card, split by hairlines (no card-in-card).
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid ${({ theme }) => theme.palette.lightBorder};
  border-radius: 12px;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.lightBorder};
  gap: 1px;

  & > div {
    border: 0;
    border-radius: 0;
    box-shadow: none;
    padding: 16px;
  }

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Breakdowns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const BreakdownBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BreakdownTitle = styled.div`
  font-size: 12.5px;
  font-weight: 500;
  color: #5D665F;
  margin-bottom: 2px;
`;

const BreakdownLine = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #0E1A13;
`;

const Muted = styled.div`
  font-size: 12px;
  color: #5D665F;
`;
